import React, { useState, useEffect } from 'react'
import { api } from '../api'
import { Tenant, User, CalculationMethod, PriceRoundingMethod } from '../types'
import { TenantUsersView } from './TenantUsersView'

interface Props {
  tenant: Tenant
  currentUser: User
  onTenantUpdated: () => void
}

export function SettingsView({ tenant, currentUser, onTenantUpdated }: Props) {
  const [activeSubTab, setActiveSubTab] = useState<'general' | 'users'>('general')

  const [name, setName] = useState(tenant.name || '')
  const [calculationMethod, setCalculationMethod] = useState<CalculationMethod>(
    tenant.default_calculation_method || 'cost_plus'
  )
  const [roundingMethod, setRoundingMethod] = useState<PriceRoundingMethod>(
    tenant.price_rounding_method || 'no_rounding'
  )
  const [logoData, setLogoData] = useState<string | null>(tenant.logo_data || null)
  const [saving, setSaving] = useState(false)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  useEffect(() => {
    setName(tenant.name || '')
    setCalculationMethod(tenant.default_calculation_method || 'cost_plus')
    setRoundingMethod(tenant.price_rounding_method || 'no_rounding')
    setLogoData(tenant.logo_data || null)
  }, [tenant])

  // Non-tenant_admin users cannot access SettingsView
  if (currentUser.role !== 'tenant_admin') {
    return null
  }

  const handleLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      setErrorMessage('Lütfen geçerli bir görsel dosyası seçin (PNG, JPG, SVG, WebP).')
      return
    }

    // Limit to ~2MB for sane DB storage
    if (file.size > 2 * 1024 * 1024) {
      setErrorMessage('Görsel boyutu en fazla 2MB olabilir.')
      return
    }

    setErrorMessage(null)
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result as string
      setLogoData(result)
    }
    reader.onerror = () => {
      setErrorMessage('Görsel okunurken bir hata oluştu.')
    }
    reader.readAsDataURL(file)
  }

  const handleRemoveLogo = () => {
    setLogoData('')
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setSuccessMessage(null)
    setErrorMessage(null)

    try {
      await api.updateCurrentTenant({
        name,
        default_calculation_method: calculationMethod,
        price_rounding_method: roundingMethod,
        logo_data: logoData || null,
      })
      setSuccessMessage('Firma ayarları başarıyla kaydedildi.')
      onTenantUpdated()
    } catch (err: any) {
      setErrorMessage(err.message || 'Ayarlar kaydedilirken bir hata oluştu.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="view-container">
      <div className="section-header-flex">
        <div>
          <h2>Firma Ayarları</h2>
        </div>
      </div>

      {/* Tabs */}
      <div className="settings-tabs-bar" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={activeSubTab === 'general'}
          className={`settings-tab-btn ${activeSubTab === 'general' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('general')}
          id="tab-settings-general"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
          </svg>
          Firma Bilgileri & Parametreler
        </button>

        <button
          type="button"
          role="tab"
          aria-selected={activeSubTab === 'users'}
          className={`settings-tab-btn ${activeSubTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('users')}
          id="tab-settings-users"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
          Kullanıcılar
        </button>
      </div>

      {activeSubTab === 'users' ? (
        <TenantUsersView tenant={tenant} currentUser={currentUser} />
      ) : (
        <>
          {successMessage && (
            <div className="alert alert-success" role="status" style={{ marginBottom: '1.25rem' }}>
              ✓ {successMessage}
            </div>
          )}

          {errorMessage && (
            <div className="alert alert-danger" role="alert" style={{ marginBottom: '1.25rem' }}>
              ✕ {errorMessage}
            </div>
          )}

          <form onSubmit={handleSave} className="settings-form-layout">
            {/* Card 1: Logo & Company Identity */}
            <div className="form-card">
              <h3 className="card-title">Firma Kimliği & Logo</h3>
              <p className="card-subtitle">
                Sol üst menüde yer alan logo ve firma adını buradan kişiselleştirebilirsiniz.
              </p>

              <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                <label htmlFor="company-name">Firma / Mağaza Adı *</label>
                <input
                  id="company-name"
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Örn: Acme Sanayi Ticaret A.Ş."
                />
              </div>

              <div className="form-group">
                <label>Firma Logosu</label>
                <div className="logo-upload-container">
                  <div className="logo-preview-box">
                    {logoData ? (
                      <img src={logoData} alt="Firma Logosu Önizleme" className="logo-preview-img" />
                    ) : (
                      <div className="logo-placeholder">
                        <svg
                          width="36"
                          height="36"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                          <circle cx="12" cy="7" r="4" />
                        </svg>
                        <span>Varsayılan Profil</span>
                      </div>
                    )}
                  </div>

                  <div className="logo-actions-col">
                    <label className="btn btn-secondary btn-file-upload">
                      <span>Görsel Yükle</span>
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/svg+xml,image/webp"
                        onChange={handleLogoFileChange}
                        style={{ display: 'none' }}
                      />
                    </label>

                    {logoData && (
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={handleRemoveLogo}
                        style={{ marginTop: '0.5rem' }}
                      >
                        Logoyu Kaldır
                      </button>
                    )}

                    <span className="text-muted" style={{ fontSize: '0.8125rem', marginTop: '0.5rem' }}>
                      Yüklenen görsel doğrudan veritabanında saklanır. PNG, JPG, SVG veya WebP formatında yükleyebilirsiniz (Maks. 2MB).
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Card 2: Pricing & Rounding Settings */}
            <div className="form-card">
              <h3 className="card-title">Fiyatlandırma & Yuvarlama Parametreleri</h3>
              <p className="card-subtitle">
                Tüm ürünler ve fiyat listelerinde varsayılan olarak uygulanacak hesaplama kuralları.
              </p>

              <div className="form-group" style={{ marginBottom: '1.75rem' }}>
                <label htmlFor="default-calc-method">Varsayılan Kâr Hesaplama Yöntemi *</label>
                <select
                  id="default-calc-method"
                  value={calculationMethod}
                  onChange={(e) => setCalculationMethod(e.target.value as CalculationMethod)}
                >
                  <option value="cost_plus">
                    Maliyet Üzerinden Kâr Hesabı (Cost-Plus Markup) — Fiyat = Maliyet × (1 + Kâr)
                  </option>
                  <option value="margin">
                    Satış Fiyatı Üzerinden Kâr Hesabı (Gross Margin) — Fiyat = Maliyet / (1 - Kâr)
                  </option>
                </select>
                <span className="text-muted" style={{ fontSize: '0.8125rem', marginTop: '0.35rem', display: 'block' }}>
                  Ürün veya kategori bazında özel kâr yöntemi belirlenmemişse bu genel kural geçerli olur.
                </span>
              </div>

              <div className="form-group">
                <label htmlFor="price-rounding-method">Nihai Satış Fiyatı Yuvarlama Yöntemi *</label>
                <select
                  id="price-rounding-method"
                  value={roundingMethod}
                  onChange={(e) => setRoundingMethod(e.target.value as PriceRoundingMethod)}
                >
                  <option value="no_rounding">Yuvarlama Yapma (Kuruş hassasiyeti korunur: ₺142,34)</option>
                  <option value="round_integer">En Yakın Tam Sayıya Yuvarla (Örn: ₺142,34 ➔ ₺142,00 / ₺142,60 ➔ ₺143,00)</option>
                  <option value="round_nearest_5">En Yakın 5'in Katı Sayıya Yuvarla (Örn: ₺142,34 ➔ ₺140,00 / ₺143,20 ➔ ₺145,00)</option>
                  <option value="round_nearest_10">En Yakın 10'un Katı Sayıya Yuvarla (Örn: ₺143,20 ➔ ₺140,00 / ₺147,80 ➔ ₺150,00)</option>
                </select>
                <span className="text-muted" style={{ fontSize: '0.8125rem', marginTop: '0.35rem', display: 'block' }}>
                  Fiyat listelerinde KDV dahil nihai satış fiyatı hesaplandıktan sonra seçilen yuvarlama kuralı uygulanır.
                </span>
              </div>
            </div>

            <div className="form-actions-bar" style={{ marginTop: '1.5rem' }}>
              <button type="submit" className="btn btn-primary btn-lg" disabled={saving}>
                {saving ? 'Kaydediliyor…' : 'Ayarları Kaydet'}
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  )
}
